"use client";

import { type ReactNode, useEffect, useRef, useState } from "react";
import type { OrgItem } from "./OrgSwitcherModal";
import { ProfileMenu } from "./ProfileMenu";

/* ── Types ──────────────────────────────────────────────────────────────── */

/** Completion state of an org-profile section, used for its severity badge. */
export type SectionState = "complete" | "yellow" | "red" | "gray";

export type OrgIssueCounts = {
  red: number;
  yellow: number;
  gray: number;
};

export type NavProduct = {
  id: string;
  name: string;
  description: string;
  /** Whether the product is currently selected. */
  active?: boolean;
  /** Whether the product is disabled / coming soon. */
  disabled?: boolean;
};

/** A single item in the primary navigation list. */
export type NavItem = {
  /** Unique identifier used as the React key. */
  id: string;
  /** Display label. */
  label: string;
  /** Icon rendered to the left of the label — typically a small SVG. */
  icon: ReactNode;
  /** Whether this item is the currently active route. */
  active?: boolean;
  /** Called when the user clicks the item. */
  onClick: () => void;
  /**
   * Optional content rendered after the label (e.g. a status badge).
   * Use `OrgStatusIndicator` for organisation-profile issue badges.
   */
  badge?: ReactNode;
};

export type Theme = "light" | "dark" | "system";

export interface SideNavProps {
  /** Navigation items rendered in the primary nav section. */
  navItems: NavItem[];
  /** Available products shown in the brand-switcher dropdown. */
  products?: NavProduct[];
  /**
   * Called when the user selects an enabled product from the switcher.
   * Receives the `id` of the chosen product.
   */
  onProductSelect?: (productId: string) => void;
  /** Profile menu props passed down to the bottom row. */
  profile: {
    name: string;
    department: string;
    initials: string;
  };
  /** Currently active colour theme. */
  theme: Theme;
  /** Called when the user picks a different theme. */
  onThemeChange: (theme: Theme) => void;
  /** Called when user clicks "Account settings". */
  onAccountSettings?: () => void;
  /** Called when user clicks "Sign out". */
  onSignOut?: () => void;
  /** Full list of organizations for the org switcher modal. When omitted the Organization section is hidden. */
  orgs?: OrgItem[];
  /**
   * Optional content between the product switcher and the primary navigation,
   * e.g. a search control. Nothing is rendered when omitted.
   */
  headerContent?: ReactNode;
  /** ID of the currently active organization. */
  activeOrgId?: string | null;
  /** Called when the user selects a different organization in the modal. */
  onOrgSelect?: (orgId: string) => void;
}

/* ── Product switcher ─────────────────────────────────────────────────── */

const DEFAULT_PRODUCTS: NavProduct[] = [
  {
    id: "compass",
    name: "Compass",
    description: "Evaluate Opportunities",
    active: true,
  },
];

function ProductSwitcher({
  products,
  onSelect,
}: {
  products: NavProduct[];
  onSelect?: (productId: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const activeProduct = products.find((p) => p.active) ?? products[0];

  useEffect(() => {
    if (!open) return;

    function onClick(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node))
        setOpen(false);
    }

    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }

    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="relative px-3" ref={wrapRef}>
      <button
        type="button"
        className={[
          "inline-flex items-center gap-1.5",
          "px-2 py-1.5 rounded-[3px]",
          "font-display text-[13.5px] font-bold tracking-[-0.01em]",
          "text-(--fg-primary) cursor-pointer",
          "transition-[background] duration-120",
          open ? "bg-(--bg-canvas)" : "hover:bg-(--bg-hover)",
        ].join(" ")}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span>{activeProduct?.name ?? "Compass"}</span>
        <svg
          className="text-(--fg-muted) shrink-0"
          width="11"
          height="11"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.4"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden={true}
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {open && (
        <div
          className={[
            "absolute top-[calc(100%+4px)] left-3 right-3 z-50",
            "bg-(--bg-white) border border-(--border-default) rounded-sm",
            "shadow-[0_8px_24px_rgba(10,16,32,0.12),0_2px_6px_rgba(10,16,32,0.06)]",
            "p-1 flex flex-col gap-0.5",
          ].join(" ")}
          role="listbox"
          aria-label="Switch product"
        >
          {products.map((p) => (
            <div
              key={p.id}
              role="option"
              aria-selected={!!p.active}
              aria-disabled={!!p.disabled}
              tabIndex={p.disabled ? -1 : 0}
              className={[
                "relative flex flex-col gap-0.5",
                "px-3 py-2.5 rounded-[3px]",
                p.disabled ? "cursor-not-allowed opacity-55" : "cursor-pointer",
                p.active && !p.disabled ? "bg-(--bg-canvas)" : "",
                !p.disabled && !p.active ? "hover:bg-(--bg-hover)" : "",
              ].join(" ")}
              onClick={() => {
                if (!p.disabled) {
                  setOpen(false);
                  onSelect?.(p.id);
                }
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  if (!p.disabled) {
                    setOpen(false);
                    onSelect?.(p.id);
                  }
                }
              }}
            >
              <span className="text-[13px] font-semibold text-(--fg-primary)">
                {p.name}
              </span>
              <span className="text-[11.5px] text-(--fg-secondary) leading-[1.4]">
                {p.description}
              </span>
              {p.disabled && (
                <span className="absolute top-2.5 right-2.5 font-mono text-[9px] font-semibold px-1.5 py-0.5 bg-(--bg-canvas) border border-(--border-subtle) rounded-[3px] uppercase tracking-[0.06em] text-(--fg-secondary)">
                  Coming soon
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Status indicator ────────────────────────────────────────────────── */

/**
 * Badge displayed after a nav item label when the organisation profile has
 * unresolved issues. Pass it as the `badge` field of a `NavItem`.
 */
export function OrgStatusIndicator({
  severity,
  counts,
}: {
  severity: SectionState;
  counts: OrgIssueCounts;
}) {
  const colorCls =
    severity === "red"
      ? "text-[var(--status-error)]"
      : severity === "yellow"
        ? "text-[var(--status-warning)]"
        : "text-[var(--fg-muted)]";

  const dotColor = (s: "red" | "yellow" | "gray") =>
    s === "red"
      ? "bg-[var(--status-error)]"
      : s === "yellow"
        ? "bg-[var(--status-warning)]"
        : "bg-[var(--fg-subtle)]";

  return (
    <span
      role="img"
      className={[
        "relative ml-auto",
        "inline-flex items-center justify-center w-5.5 h-5.5 shrink-0",
        colorCls,
        "cursor-help",
        "group",
      ].join(" ")}
      aria-label="Organization profile has unresolved issues"
    >
      <svg
        width="13"
        height="13"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden={true}
      >
        <path d="M12 4L22 20H2L12 4Z" />
        <path d="M12 11v3" />
        <path d="M12 17h.01" />
      </svg>

      {/* Tooltip */}
      <span
        className={[
          "absolute top-1/2 left-[calc(100%+12px)]",
          "-translate-y-1/2 translate-x-1",
          "group-hover:translate-x-0 group-focus-visible:translate-x-0",
          "w-60 p-3",
          "bg-(--bg-white) border border-(--border-default) rounded-[3px]",
          "shadow-[0_8px_20px_rgba(10,16,32,0.12),0_2px_6px_rgba(10,16,32,0.06)]",
          "font-body font-normal text-[11.5px] leading-normal text-(--fg-secondary)",
          "invisible opacity-0 group-hover:visible group-hover:opacity-100 group-focus-visible:visible group-focus-visible:opacity-100",
          "transition-[opacity,transform,visibility] duration-120",
          "z-30 pointer-events-none whitespace-normal",
        ].join(" ")}
        role="tooltip"
      >
        <span className="block text-[10.5px] font-semibold uppercase tracking-[0.06em] text-(--fg-muted) mb-1.5">
          Organization issues
        </span>
        <ul className="list-none p-0 m-0 flex flex-col gap-1">
          {counts.red > 0 && (
            <li className="flex items-center gap-2">
              <span
                className={`w-1.75 h-1.75 rounded-[3px] shrink-0 ${dotColor("red")}`}
                aria-hidden={true}
              />
              {counts.red} {counts.red === 1 ? "section" : "sections"} with
              field errors
            </li>
          )}
          {counts.yellow > 0 && (
            <li className="flex items-center gap-2">
              <span
                className={`w-1.75 h-1.75 rounded-[3px] shrink-0 ${dotColor("yellow")}`}
                aria-hidden={true}
              />
              {counts.yellow} {counts.yellow === 1 ? "section" : "sections"}{" "}
              affecting opportunities
            </li>
          )}
          {counts.gray > 0 && (
            <li className="flex items-center gap-2">
              <span
                className={`w-1.75 h-1.75 rounded-[3px] shrink-0 ${dotColor("gray")}`}
                aria-hidden={true}
              />
              {counts.gray} {counts.gray === 1 ? "section" : "sections"} with
              missing data
            </li>
          )}
        </ul>
      </span>
    </span>
  );
}

/* ── SideNav (public export) ─────────────────────────────────────────── */

/**
 * Primary left-hand navigation sidebar. Renders the product brand switcher at
 * the top, configurable nav links in the middle, and the profile menu at the
 * bottom. Width is fixed at 240 px.
 */
export function SideNav({
  navItems,
  products = DEFAULT_PRODUCTS,
  onProductSelect,
  profile,
  theme = "system",
  onThemeChange,
  onAccountSettings,
  onSignOut,
  orgs,
  headerContent,
  activeOrgId,
  onOrgSelect,
}: SideNavProps) {
  const [profileOpen, setProfileOpen] = useState(false);
  const profileRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!profileOpen) return;

    function onDown(e: MouseEvent) {
      if (!profileRef.current?.contains(e.target as Node))
        setProfileOpen(false);
    }

    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setProfileOpen(false);
    }

    window.addEventListener("mousedown", onDown);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", onDown);
      window.removeEventListener("keydown", onKey);
    };
  }, [profileOpen]);

  const linkCls = (isActive: boolean) =>
    [
      "inline-flex items-center gap-2.5",
      "w-full h-[34px] px-3",
      "text-[13px] font-medium",
      "rounded-[3px] text-decoration-none box-border cursor-pointer",
      "transition-[background,color] duration-[120ms]",
      isActive
        ? "bg-[var(--bg-hover)] text-[var(--fg-primary)]"
        : "text-[var(--fg-secondary)] hover:bg-[var(--bg-hover)] hover:text-[var(--fg-primary)]",
    ].join(" ");

  return (
    <aside
      className={[
        "shrink-0 w-60",
        "bg-(--bg-white) border-r border-(--border-subtle)",
        "flex flex-col",
        "pt-6 gap-4",
      ].join(" ")}
      aria-label="Sidebar"
    >
      {/* Brand / product switcher */}
      {products.length > 0 && (
        <ProductSwitcher products={products} onSelect={onProductSelect} />
      )}

      {headerContent && <div className="px-3">{headerContent}</div>}

      {/* Nav links */}
      <nav className="flex flex-col gap-0.5 px-3" aria-label="Primary">
        {navItems.map((item) => (
          <button
            key={item.id}
            type="button"
            className={linkCls(!!item.active)}
            aria-current={item.active ? "page" : undefined}
            onClick={item.onClick}
          >
            <span
              className={`inline-flex items-center justify-center shrink-0 ${item.active ? "text-(--fg-primary)" : "text-(--fg-muted)"}`}
              aria-hidden={true}
            >
              {item.icon}
            </span>
            {item.label}
            {item.badge}
          </button>
        ))}
      </nav>

      {/* Profile menu */}
      <ProfileMenu
        variant="side"
        name={profile.name}
        initials={profile.initials}
        department={profile.department}
        theme={theme}
        onThemeChange={onThemeChange}
        onSignOut={onSignOut}
        onAccountSettings={onAccountSettings}
        orgs={orgs}
        activeOrgId={activeOrgId}
        onOrgSelect={onOrgSelect}
      />
    </aside>
  );
}
